import * as React from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'wouter';
import { useRecoilState, useRecoilValue } from 'recoil';
import PermissionsService, { PERMISSION_AUTO_REPORT } from '../../../Services/PermissionsService';
import TableService, {
  TABLE_SUPPLIER,
  TABLE_OPERATING_COSTS,
  TABLE_OPERATING_COSTS_TYPES,
  TABLE_GDPR_PROFILES,
  TABLE_PERIOD_LOCK_DEFINITION,
  TABLE_CODEBOOKS_RIDE,
  TABLE_CODEBOOKS_PARKING,
  TABLE_ALARM,
  TABLE_AUTO_REPORT,
  TABLE_PERSON,
  TABLE_TEMPLATES,
  TABLE_REFUELING,
  TABLE_CORRECTIONS,
} from '../../../Services/TableService';
import * as SubpageState from '../../../store/recoil/SubpageState';
import { URL_EXPENSE_IMPORT } from '../../../router/constants';
import LoaderService from '../../../Services/LoaderService';
import { showMessage } from '../../../Services/ToastService';
import { selectedVehiclesAtom } from '../../../store/recoil/vehicles';

const RESPONSE_OK = 'ok';

const getFormUrl = (tableName: string) => {
  return `${tableName}/form`;
};

interface IComponentProps {
  tableName: string;
  buttons: any;
  handleForm?: (data: any, isEdit: boolean) => void;
  openForm?: (data: any, isEdit: boolean) => void;
}

function TableRecordAdding(props: IComponentProps): JSX.Element {
  const [pathname, navigate] = useLocation();
  const { formatMessage: f } = useIntl();
  const selectedVehicles = useRecoilValue<number[]>(selectedVehiclesAtom);
  const [isRequireWizard, setIsRequireWizard] = React.useState<boolean>(false);
  const [isSubPageOpen, setIsSubPageOpen] = useRecoilState<boolean>(SubpageState.isSubPageOpen);

  const getIsCorrectionTableWithVehicles = (isButtonClicked?: boolean) => {
    if (props.tableName !== TABLE_CORRECTIONS) {
      return true;
    }
    if (Object.values(selectedVehicles).length > 0) {
      return true;
    }
    showMessage(
      f({ id: 'toastr.warning' }),
      f({ id: 'tripsOverview.modal.newRide.vehicleId.error' }),
      'warning'
    );
    return !isButtonClicked;
  };

  const handleOpenModalWithLink = React.useCallback(async () => {
    const { tableName, handleForm, openForm } = props;

    setIsSubPageOpen(true);

    if (
      (tableName === TABLE_SUPPLIER ||
        tableName === TABLE_OPERATING_COSTS ||
        tableName === TABLE_OPERATING_COSTS_TYPES ||
        tableName === TABLE_GDPR_PROFILES ||
        tableName === TABLE_ALARM) &&
      handleForm
    ) {
      handleForm(null, false);
    } else if (openForm) {
      openForm(null, false);
    } else if (tableName !== TABLE_REFUELING && handleForm && getIsCorrectionTableWithVehicles()) {
      LoaderService.showLoader();
      const response = await TableService.getForm(getFormUrl(tableName));
      if (response && response.status === RESPONSE_OK) {
        handleForm(response.form.inputs, false);
      }
    }
    LoaderService.showLoader(false);
  }, [props, setIsSubPageOpen]);

  React.useEffect(() => {
    if (pathname.includes('add') || (!isSubPageOpen && pathname.includes('add'))) {
      handleOpenModalWithLink();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubPageOpen, props.tableName, setIsSubPageOpen, pathname]);

  React.useEffect(() => {
    if (isRequireWizard && pathname === `${URL_EXPENSE_IMPORT}/expense/import/record`) {
      navigate(`${URL_EXPENSE_IMPORT}/wizard`);
    }
  }, [isRequireWizard, navigate, pathname]);

  const handleOpenAddDataModal = async (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();

    const { tableName, handleForm, openForm } = props;

    if (
      (tableName === TABLE_SUPPLIER ||
        tableName === TABLE_OPERATING_COSTS ||
        tableName === TABLE_OPERATING_COSTS_TYPES ||
        tableName === TABLE_GDPR_PROFILES ||
        tableName === TABLE_ALARM ||
        tableName === TABLE_PERSON) &&
      handleForm
    ) {
      handleForm(null, false);
    } else if (openForm) {
      openForm(null, false);
    } else if (handleForm && getIsCorrectionTableWithVehicles(true)) {
      LoaderService.showLoader();
      const response = await TableService.getForm(getFormUrl(tableName));

      if (response && response.status === RESPONSE_OK) {
        handleForm(response.form.inputs, false);
      }
      LoaderService.showLoader(false);
    }
  };

  const handleOpenWizard = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    if (event.currentTarget) {
      setIsRequireWizard(true);
    }
  };

  const handleAddOnlineTemplate = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    window.location.href = '/online-templates/add';
  };

  const { tableName } = props;

  const handleOpenAddData = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    const button = event.currentTarget.getAttribute('data-id');

    const tempPath = pathname.split('/');

    if (
      pathname.substring(pathname.length - 4, pathname.length) !== '/add' &&
      tableName !== TABLE_TEMPLATES
    ) {
      switch (tableName) {
        case TABLE_CODEBOOKS_RIDE:
          navigate(`/${tempPath[1]}/${props.tableName}/add`);
          break;
        case TABLE_CODEBOOKS_PARKING:
          navigate(`/${tempPath[1]}/${props.tableName}/add`);
          break;
        case TABLE_PERIOD_LOCK_DEFINITION:
          navigate(`/${tempPath[1]}/definition/add`);
          break;
        default:
          if (button !== 'wizardBtn' && getIsCorrectionTableWithVehicles(true)) {
            navigate(`/${tempPath[1]}/add`);
          }
          break;
      }
    }

    switch (button) {
      case 'addBtn':
        handleOpenAddDataModal(event);
        break;
      case 'gdprProfile.addProfile':
        handleOpenAddDataModal(event);
        break;
      case 'addSetting':
        handleOpenAddDataModal(event);
        break;
      case 'wizardBtn':
        handleOpenWizard(event);
        break;
      case 'addSupplier':
        handleOpenAddDataModal(event);
        break;
      case 'addCostsOverview':
        handleOpenAddDataModal(event);
        break;
      case 'addOperatingCostType':
        handleOpenAddDataModal(event);
        break;
      case 'addAlarm':
        handleOpenAddDataModal(event);
        break;
      case 'addOnlineTemplate':
        handleOpenAddDataModal(event);
        break;
      case 'addTemplateBtn':
        handleAddOnlineTemplate(event);
        break;
      default:
        console.error('No action');
    }
  };

  React.useEffect(() => {
    if (pathname.includes('/expense/add') || pathname.includes('/suppliers/add')) {
      if (props.handleForm) {
        props.handleForm(null, false);
      }
    }
  }, [pathname, props]);

  return (
    <div>
      {(tableName === TABLE_AUTO_REPORT
        ? PermissionsService.hasAccess(`${PERMISSION_AUTO_REPORT}01`, ['create']) ||
          PermissionsService.hasAccess(`${PERMISSION_AUTO_REPORT}02`, ['create']) ||
          PermissionsService.hasAccess(`${PERMISSION_AUTO_REPORT}03`, ['create']) ||
          PermissionsService.hasAccess(`${PERMISSION_AUTO_REPORT}04`, ['create']) ||
          PermissionsService.hasAccess(`${PERMISSION_AUTO_REPORT}05`, ['create']) ||
          PermissionsService.hasAccess(`${PERMISSION_AUTO_REPORT}06`, ['create']) ||
          PermissionsService.hasAccess(`${PERMISSION_AUTO_REPORT}07`, ['create'])
        : PermissionsService.hasAccess(tableName, ['create'])) &&
        props.buttons.open.map((button: string, index: number) => {
          return (
            <a
              key={`${button}-${index}`}
              href="#"
              data-cy={`table-record-${button}`}
              title={f({ id: `table.${button}` })}
              className="e-button e-button--gray mb-4"
              onClick={handleOpenAddData}
              style={{ marginRight: '7px' }}
              data-id={button}
            >
              {button && button.split('.').length === 2
                ? f({ id: `${button}` })
                : f({ id: `table.${button}` })}
            </a>
          );
        })}
    </div>
  );
}

export default TableRecordAdding;
